import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useMemo,
    useEffect,
    useRef,
} from 'react'
import { useLocation } from 'react-router-dom'
import { useHelpBot } from '../api/hooks/helpBot/useHelpBot'
import { EVENTS, trackEvent } from '../events'
import { ROUTES } from '../router/routes'

const HelpBotContext = createContext(null)

const STORAGE_KEYS = {
    SESSION_ID: 'helpBot_sessionId',
    MESSAGES: 'helpBot_messages',
    PANEL_SIZE: 'helpBot_panelSize',
    POSITION: 'helpBot_position',
    HAS_SEEN_WELCOME: 'helpBot_hasSeenWelcome',
}

const MAX_MESSAGES = 30

// Routes where auto-open should be skipped
const AUTO_OPEN_EXCLUDED_ROUTES = [ROUTES.ONBOARDING, ROUTES.INSIGHTS]

export const PANEL_SIZES = {
    DEFAULT: 'default',
    LARGE: 'large',
    WIDE: 'wide',
}

export const POSITIONS = {
    RIGHT: 'right',
    LEFT: 'left',
}

const generateSessionId = () => {
    const timestamp = Date.now()
    const random = Math.random().toString(36).substring(2, 8)
    return `sess_${random}_${timestamp}`
}

const trimMessages = (messages) => {
    if (messages.length <= MAX_MESSAGES) return messages
    return messages.slice(-MAX_MESSAGES)
}

export const HelpBotProvider = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false)
    const { sendMessage, isPending } = useHelpBot()
    const location = useLocation()

    const [sessionId, setSessionId] = useState(() => {
        const stored = localStorage.getItem(STORAGE_KEYS.SESSION_ID)
        if (stored) return stored
        const newId = generateSessionId()
        localStorage.setItem(STORAGE_KEYS.SESSION_ID, newId)
        return newId
    })

    const [messages, setMessages] = useState(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEYS.MESSAGES)
            return stored ? JSON.parse(stored) : []
        } catch {
            return []
        }
    })

    const [hasUnread, setHasUnread] = useState(false)
    const [panelSize, setPanelSize] = useState(() => {
        const stored = localStorage.getItem(STORAGE_KEYS.PANEL_SIZE)
        return stored && Object.values(PANEL_SIZES).includes(stored) ? stored : PANEL_SIZES.DEFAULT
    })
    const [position, setPosition] = useState(() => {
        const stored = localStorage.getItem(STORAGE_KEYS.POSITION)
        return stored && Object.values(POSITIONS).includes(stored) ? stored : POSITIONS.LEFT
    })
    const isOpenRef = useRef(isOpen)

    useEffect(() => {
        isOpenRef.current = isOpen
    }, [isOpen])

    const markAsRead = useCallback(() => {
        setHasUnread(false)
    }, [])

    const changePanelSize = useCallback((size) => {
        if (Object.values(PANEL_SIZES).includes(size)) {
            setPanelSize(size)
            localStorage.setItem(STORAGE_KEYS.PANEL_SIZE, size)
        }
    }, [])

    const changePosition = useCallback((pos) => {
        if (Object.values(POSITIONS).includes(pos)) {
            setPosition(pos)
            localStorage.setItem(STORAGE_KEYS.POSITION, pos)
        }
    }, [])

    useEffect(() => {
        localStorage.setItem(STORAGE_KEYS.MESSAGES, JSON.stringify(messages))
    }, [messages])

    // First-visit welcome - auto-open panel (empty state shows welcome content)
    // Skip auto-open if user is on excluded routes
    useEffect(() => {
        const isExcludedRoute = AUTO_OPEN_EXCLUDED_ROUTES.includes(location.pathname)
        if (isExcludedRoute) return

        const hasSeenWelcome = localStorage.getItem(STORAGE_KEYS.HAS_SEEN_WELCOME)
        if (!hasSeenWelcome) {
            // Mark as seen immediately to prevent re-triggering
            localStorage.setItem(STORAGE_KEYS.HAS_SEEN_WELCOME, 'true')
            // Delay opening to let the app fully render
            const timer = setTimeout(() => {
                setIsOpen(true)
            }, 1000)
            return () => clearTimeout(timer)
        }
    }, [location.pathname])

    const openChat = useCallback(() => {
        trackEvent(EVENTS.HELPBOT.OPEN)
        setIsOpen(true)
        setHasUnread(false)
    }, [])
    const closeChat = useCallback(() => setIsOpen(false), [])
    const toggleChat = useCallback(() => {
        setIsOpen((prev) => {
            if (!prev) {
                // Opening the panel - mark as read
                trackEvent(EVENTS.HELPBOT.OPEN)
                setHasUnread(false)
            }
            return !prev
        })
    }, [])

    const addMessage = useCallback((message) => {
        setMessages((prev) =>
            trimMessages([
                ...prev,
                { ...message, id: Date.now(), timestamp: new Date().toISOString() },
            ])
        )
        if (message.role === 'assistant' && !isOpenRef.current) {
            setHasUnread(true)
        }
    }, [])

    const clearMessages = useCallback(() => {
        setMessages([])
        const newId = generateSessionId()
        setSessionId(newId)
        localStorage.setItem(STORAGE_KEYS.SESSION_ID, newId)
    }, [])

    const getHistoryForApi = useCallback(() => {
        return messages.map((msg) => ({
            role: msg.role,
            content: msg.content,
        }))
    }, [messages])

    // Ref to access latest messages in sendPrompt without stale closure
    const messagesRef = useRef(messages)
    useEffect(() => {
        messagesRef.current = messages
    }, [messages])

    const sendPrompt = useCallback(
        (text) => {
            // Open the chat panel
            setIsOpen(true)
            setHasUnread(false)

            // Add user message
            setMessages((prev) =>
                trimMessages([
                    ...prev,
                    {
                        role: 'user',
                        content: text,
                        id: Date.now(),
                        timestamp: new Date().toISOString(),
                    },
                ])
            )

            // Get history including the new message
            const history = [
                ...messagesRef.current.map((msg) => ({ role: msg.role, content: msg.content })),
                { role: 'user', content: text },
            ]

            // Send to API
            sendMessage({
                question: text,
                history,
                sessionId,
                onSuccess: (response) => {
                    setMessages((prev) =>
                        trimMessages([
                            ...prev,
                            {
                                role: 'assistant',
                                content: response.answer,
                                id: Date.now(),
                                timestamp: new Date().toISOString(),
                            },
                        ])
                    )
                    trackEvent(EVENTS.HELPBOT.RESPONSE, { question: text })
                },
                onError: (errorMessage) => {
                    setMessages((prev) =>
                        trimMessages([
                            ...prev,
                            {
                                role: 'assistant',
                                content: errorMessage,
                                id: Date.now(),
                                timestamp: new Date().toISOString(),
                            },
                        ])
                    )
                    trackEvent(EVENTS.HELPBOT.RESPONSE, {
                        question: text,
                        error: errorMessage,
                    })
                },
            })
        },
        [sendMessage, sessionId]
    )

    const value = useMemo(
        () => ({
            isOpen,
            sessionId,
            messages,
            hasUnread,
            panelSize,
            position,
            isPending,
            openChat,
            closeChat,
            toggleChat,
            addMessage,
            clearMessages,
            getHistoryForApi,
            markAsRead,
            changePanelSize,
            changePosition,
            sendPrompt,
        }),
        [
            isOpen,
            sessionId,
            messages,
            hasUnread,
            panelSize,
            position,
            isPending,
            openChat,
            closeChat,
            toggleChat,
            addMessage,
            clearMessages,
            getHistoryForApi,
            markAsRead,
            changePanelSize,
            changePosition,
            sendPrompt,
        ]
    )

    return <HelpBotContext.Provider value={value}>{children}</HelpBotContext.Provider>
}

export const useHelpBotContext = () => {
    const context = useContext(HelpBotContext)
    if (!context) {
        throw new Error('useHelpBotContext must be used within HelpBotProvider')
    }
    return context
}
